import React, { useMemo, useEffect } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useEmployeeShiftData } from '@/hooks/useEmployeeShiftData';
import { useEmployeeDutyRosterStatus } from '@/hooks/useDutyRosterEmployeeData';
import { EmployeeShiftSkeleton } from '@/components/ui/SkeletonLoader';
import { hasShiftWorkingHoursForDate } from '@/utils/shiftFormUtils';
import { DayTimeDeduction } from '@/types';
import ShiftInfoTable from './ShiftInfoTable';
import DutyRosterEmployeeInfo from './DutyRosterEmployeeInfo';

interface EmployeeShiftInfoProps {
  employeeId: string | null;
  employeeName?: string;
  otDate?: string; // OT date in YYYY-MM-DD format
  className?: string;
  onShiftSelectionNeeded?: (needed: boolean) => void;
  onShiftDataLoaded?: (hasWorkingHours: boolean) => void;
}

// Type guard to validate DayTimeDeduction structure
function isValidDayTimeDeduction(data: unknown): data is DayTimeDeduction[] {
  if (!Array.isArray(data)) return false;
  return data.every(item => 
    typeof item === 'object' && 
    item !== null &&
    'day' in item &&
    'start_time' in item &&
    'end_time' in item &&
    'break_hrs' in item
  );
}

export const EmployeeShiftInfo: React.FC<EmployeeShiftInfoProps> = ({
  employeeId,
  employeeName,
  otDate,
  className = '',
  onShiftSelectionNeeded,
  onShiftDataLoaded
}) => {
  // Check if employee is duty roster
  const { data: dutyRosterStatus, isLoading: dutyRosterLoading } = useEmployeeDutyRosterStatus(employeeId);
  
  // For non-duty roster employees, use existing logic
  const { data: shiftData, isLoading: shiftLoading, error: shiftError } = useEmployeeShiftData(
    employeeId, 
    otDate
  );
  
  const isLoading = dutyRosterLoading || (dutyRosterStatus?.isDutyRoster === false && shiftLoading);
  
  const displayError = shiftError ? 'Failed to load shift information' : 
    (!shiftData && employeeId && !shiftLoading && dutyRosterStatus?.isDutyRoster === false ? 'No active shift assignment found' : null);

  // Check if shift has working hours for the OT date (only for non-duty roster)
  const hasWorkingHoursForDate = useMemo(() => {
    if (!shiftData?.shifts?.day_time_deduction || !otDate || dutyRosterStatus?.isDutyRoster) return false;
    
    // Validate and convert the day_time_deduction to DayTimeDeduction[]
    const dayTimeDeduction = shiftData.shifts.day_time_deduction;
    if (!isValidDayTimeDeduction(dayTimeDeduction)) return false;
    
    return hasShiftWorkingHoursForDate(dayTimeDeduction, otDate);
  }, [shiftData, otDate, dutyRosterStatus?.isDutyRoster]);

  // Notify parent component when shift selection is needed
  useEffect(() => {
    if (onShiftSelectionNeeded && !isLoading && employeeId && otDate && dutyRosterStatus) {
      if (dutyRosterStatus.isDutyRoster) {
        // For duty roster employees, always show shift selection
        onShiftSelectionNeeded(true);
      } else {
        // For non-duty roster employees, use existing logic
        const needsShiftSelection = !shiftData?.shifts;
        onShiftSelectionNeeded(needsShiftSelection);
      }
    }
  }, [shiftData, isLoading, employeeId, otDate, onShiftSelectionNeeded, dutyRosterStatus]);

  // Notify parent when shift data is loaded and working hours status is known
  useEffect(() => {
    if (onShiftDataLoaded && !isLoading && employeeId) {
      // Only signal that shift data has loaded when there's an actual employee
      onShiftDataLoaded(true);
    }
  }, [isLoading, onShiftDataLoaded, employeeId]);

  // Don't render if no employee is selected
  if (!employeeId) {
    return null;
  }

  // If employee is duty roster, use the new component
  if (dutyRosterStatus?.isDutyRoster) {
    return (
      <DutyRosterEmployeeInfo
        employeeId={employeeId}
        employeeName={employeeName}
        otDate={otDate}
        className={className}
        onShiftSelectionNeeded={onShiftSelectionNeeded}
        onShiftDataLoaded={onShiftDataLoaded}
      />
    );
  }

  // For non-duty roster employees, use existing logic
  return (
    <div className={`${className} w-full`}>
        {isLoading ? (
          <EmployeeShiftSkeleton />
        ) : displayError ? (
          <div className="flex items-center gap-2 py-1">
            <AlertTriangle className="h-4 w-4 text-amber-500 dark:text-amber-400" />
            <span className="text-xs text-amber-600 dark:text-amber-400">{displayError}</span>
          </div>
        ) : shiftData?.shifts ? (
          <div className="w-full space-y-2">
            {/* Use the new reusable ShiftInfoTable component */}
            <ShiftInfoTable 
              shiftData={{
                name: shiftData.shifts.name,
                day_time_deduction: isValidDayTimeDeduction(shiftData.shifts.day_time_deduction) 
                  ? shiftData.shifts.day_time_deduction 
                  : []
              }}
              otDate={otDate || ''}
              showNoWorkingHoursMessage={!hasWorkingHoursForDate}
            />
          </div>
        ) : (
          <div className="flex items-center gap-2 py-1">
            <AlertTriangle className="h-4 w-4 text-amber-500 dark:text-amber-400" />
            <span className="text-xs text-amber-600 dark:text-amber-400">No active shift assignment found for this employee</span>
          </div>
        )}
    </div>
  );
};

export default EmployeeShiftInfo;